import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock';

/** Below this width the nav is a full screen overlay, so page scrolling gets locked */
const DESKTOP_QUERY = '(min-width: 48rem)';

/** Keys that scroll the page, blocked while locked */
const SCROLL_KEYS = [
    ' ',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp',
];

/** Elements that legitimately handle the keys above themselves */
const INTERACTIVE = 'input, textarea, select, button, [contenteditable], [tabindex]';

/**
 * Mirrors tua-body-scroll-lock's own iOS sniffing so we branch exactly the way
 * it would internally, with no gap or overlap between the two code paths.
 */
const isIOS = () => {
    const ua = navigator.userAgent;
    const ipad = /(iPad).*OS\s([\d_]+)/.test(ua);
    const iphone = !ipad && /(iPhone\sOS)\s([\d_]+)/.test(ua);

    return ipad || iphone;
};

/**
 * Body scroll lock, only applied on small screens (below 48rem / 768px).
 * Anything from the tablet breakpoint up keeps its native scrolling.
 *
 * The header is `position: sticky`, and sticky only survives while the document
 * keeps a live, scrollable scrollport. Both of the usual lock techniques destroy
 * that: `position: fixed` on the body (tua-body-scroll-lock's Android strategy)
 * shifts the body up by the scroll offset, and `overflow: hidden` on the root
 * makes browsers treat `html` as the nearest scrollport with an offset of 0.
 * Either way the sticky header resolves to the top of the document instead of the
 * viewport and disappears off the top of the screen, taking the absolutely
 * positioned nav inside it along.
 *
 * So this never changes a layout property. Instead it blocks the input that would
 * scroll the page, which is what tua-body-scroll-lock already does on iOS, and
 * what we replicate for every other platform. Events inside the locked element are
 * left alone so the nav panel scrolls; `overscroll-behavior: contain` on that panel
 * keeps its scrolling from chaining out to the page.
 */
class BodyScrollLock {
    constructor() {
        this.query = window.matchMedia(DESKTOP_QUERY);
        this.target = null;
        this.locked = false;

        this.onWheel = this.onWheel.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        // Drop the lock if the viewport grows past the mobile breakpoint
        this.query.addEventListener('change', () => {
            if (this.query.matches) this.unlock();
        });
    }

    get isMobile() {
        return !this.query.matches;
    }

    get isLocked() {
        return this.locked;
    }

    /**
     * Lock page scrolling.
     * @param {HTMLElement|null} element Element that should still scroll
     */
    lock(element = null) {
        if (!this.isMobile || this.locked) return;

        this.target = element;
        this.locked = true;

        if (isIOS()) {
            lock(element);
            return;
        }

        this.addListeners();
    }

    /**
     * Release the lock and restore page scrolling.
     */
    unlock() {
        if (!this.locked) return;

        const element = this.target;
        this.target = null;
        this.locked = false;

        if (isIOS()) {
            unlock(element);
            return;
        }

        this.removeListeners();
    }

    /**
     * Force clear every lock, whatever state we are in.
     */
    clear() {
        this.target = null;
        this.locked = false;
        this.removeListeners();
        clearBodyLocks();
    }

    addListeners() {
        window.addEventListener('wheel', this.onWheel, { passive: false, capture: true });
        window.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true });
        window.addEventListener('keydown', this.onKeydown, { capture: true });
    }

    removeListeners() {
        window.removeEventListener('wheel', this.onWheel, { capture: true });
        window.removeEventListener('touchmove', this.onTouchMove, { capture: true });
        window.removeEventListener('keydown', this.onKeydown, { capture: true });
    }

    /**
     * Whether an event happened inside the element that is allowed to scroll.
     * @param {Event} event
     */
    isInsideTarget(event) {
        return !!this.target && this.target.contains(event.target);
    }

    onWheel(event) {
        if (this.isInsideTarget(event)) return;

        if (event.cancelable) event.preventDefault();
    }

    onTouchMove(event) {
        // Leave multi touch alone so pinch zoom keeps working
        if (event.touches.length > 1) return;
        if (this.isInsideTarget(event)) return;

        if (event.cancelable) event.preventDefault();
    }

    onKeydown(event) {
        if (!SCROLL_KEYS.includes(event.key)) return;
        if (this.isInsideTarget(event)) return;
        if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;

        event.preventDefault();
    }
}

export default new BodyScrollLock();
